/**
 * NotificationOrderRedirect — редирект с push-уведомления на нужный
 * маршрут заявки в зависимости от роли текущего пользователя (1.32.0).
 *
 * Зачем: SW отдаёт URL уведомления при отправке push. Но в момент
 * отправки мы не знаем роль конкретного получателя (push может прийти
 * сразу и админу, и диспетчеру). Чтобы не плодить отдельные URL для
 * каждой роли и не угадывать, шлём всех на /n/order/:id, и здесь уже
 * по фактической роли отправляем на правильный экран:
 *
 *   • technician     → /tech/order/:id
 *   • dispatcher     → /dispatch/order/:id
 *   • company_admin  → /admin/order/:id
 *   • superadmin     → /admin/order/:id (там же управление)
 *
 * Если роль ещё не загружена (AuthBoundary над нами уже гарантирует
 * наличие user, но companyRole резолвится отдельным хуком) — показываем
 * Spinner.
 */

import { useEffect } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '../design-system/atoms/Spinner';
import { useCompanyRole } from '../domains/auth/companyRole';

export function NotificationOrderRedirect() {
  const { id } = useParams<{ id: string }>();
  const { companyRole, loading } = useCompanyRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !id) return;
    let target: string;
    switch (companyRole) {
      case 'technician':
        target = `/tech/order/${id}`;
        break;
      case 'dispatcher':
        target = `/dispatch/order/${id}`;
        break;
      case 'company_admin':
        target = `/admin/order/${id}`;
        break;
      default:
        // Роль не определена (новый юзер без membership, либо ошибка):
        // ведём на корень, AuthBoundary разберётся куда дальше.
        target = '/';
    }
    navigate(target, { replace: true });
  }, [companyRole, loading, id, navigate]);

  if (!id) return <Navigate to="/" replace />;
  return <Spinner label="Открываем заявку..." />;
}
